import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { constantsData, selectionsData } from "@/data";
import { getArtistRolesListQuery } from "@/graph";
import { openAlert, setSelection } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useArtistRolesList = (isPaginated = true, hasAutoCompleteMenu = false, filters = {}, searchValue) => {
    const [fetched, setFetched] = useState(false);
    const [page, setPage] = useState(1);

    const [limit, setLimit] = useState(25);

    const dispatchedDataRef = useRef(null);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const selections = useSelector((state) => state.selectionsReducer);

    const { isAuthenticated } = useAuth0();

    const { roleIds: roleIdsKey } = selectionsData.keys;

    const { data, error, loading, refetch } = useQuery(getArtistRolesListQuery, {
        skip: !isAuthenticated,
        variables: {
            ...(isPaginated && {
                limit,
                page: page - 1,
            }),
            orderBy: { role_name: "asc" }, //eslint-disable-line
            where: {
                ...filters,
                ...(searchValue && {
                    role_name: { contains: searchValue }, //eslint-disable-line
                }),
            },
        },
    });

    if (data?.listArtistRoles && dispatchedDataRef.current !== data) {
        dispatchedDataRef.current = data;

        if (!isPaginated || hasAutoCompleteMenu) {
            const fetchedList = data.listArtistRoles.posts?.map(({ id, name }) => ({
                label: name,
                value: id,
            }));

            dispatch(
                setSelection({
                    [roleIdsKey]:
                        selections?.[roleIdsKey]?.length > 0 && hasAutoCompleteMenu
                            ? selections?.[roleIdsKey].concat(fetchedList)
                            : fetchedList,
                }),
            );
        }

        setFetched(true);

        const currentPage = data.listArtistRoles.meta?.currentPage ?? 0;
        setPage(currentPage + 1);
    }

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
            }
        },
        [error, data], // eslint-disable-line
    );

    return {
        count: data?.listArtistRoles?.meta?.resultCount,
        data: data?.listArtistRoles?.posts,
        error,
        fetched,
        limit,
        loading,
        nextCursor: data?.listArtistRoles?.meta?.nextCursor,
        page,
        prevCursor: data?.listArtistRoles?.meta?.prevCursor,
        refetch: refetch || (() => {}),
        searchKey: "role_name",
        setFetched,
        setLimit,
        setPage,
    };
};

export default useArtistRolesList;
